<template>
  <RCard class="links">
    <FormCreateLink
      :showing="showForm"
      :namespace="serviceNamespace"
      :editable="editable"
      :selected-categories="selectedCategoryIds"
      @refresh="getLinks"
      @close="closeForm"
      @categories-options="setCategoriesOptions"
    />

    <Grid gaf="column" justify-content="space-between" align-items="center">
      <div class="text-subtitle-1 text-secondary-2">Links</div>

      <Grid gaf="column" align-items="center" gap="var(--r-space-md)">
        <PlusIcon @click.native="showLinkForm" />

        <RDropdown
          v-model="selectedCategories"
          :options="categoriesOptions"
          :loading="!categoriesOptions.length"
          :custom-has-value="!areAllOptionsSelected"
          option-label="label"
          option-value="name"
          placeholder="All"
          multiple
          emit-value
          map-options
          no-label
          no-hint
        >
          <template #before-options>
            <q-item clickable @click="handleAllOptionClick">
              <q-item-section side>
                <RCheckbox class="no-pointer-events" :value="areAllOptionsSelected" :val="null" />
              </q-item-section>

              <q-item-section>
                <q-item-label>All</q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </RDropdown>
      </Grid>
    </Grid>

    <GridTable :columns="tableColumns" :rows="tableRows">
      <template #row="{row}">
        <GridTableCell v-if="row.isGroup" full-row class="text-center" no-tooltip>
          <strong>{{ row.data.entity | capitalize }} links </strong>
          <router-link :to="{ name: row.data.entity, params: { id: row.data.id } }" class="text-tertiary">
            ({{ row.data.entity_name }})
          </router-link>
        </GridTableCell>

        <template v-else>
          <GridTableCell>
            {{ row.name }}
          </GridTableCell>

          <GridTableCell no-tooltip>
            <a :href="row.url" class="link text-body-1 text-link-blue" target="_blank" rel="noopener noreferrer">
              {{ row.url }}
            </a>
            <LmTooltip>{{ row.url }}</LmTooltip>
          </GridTableCell>

          <GridTableCell no-tooltip>
            <Grid v-if="row.username" gaf="column" justify-content="space-between" align-items="center">
              <span class="text-ellipsis">{{ row.username }}</span>
              <q-btn size="xs" flat round icon="content_copy" @click="copyToClipboard(row.username)">
                <LmTooltip>Copy Username</LmTooltip>
              </q-btn>
            </Grid>

            <span v-else>N/A</span>
          </GridTableCell>

          <GridTableCell no-tooltip>
            <Grid v-if="row.password" gaf="column" justify-content="space-between" align-items="center">
              <span class="text-ellipsis">{{ row.password }}</span>
              <q-btn size="xs" flat round icon="content_copy" @click="copyToClipboard(row.password)">
                <LmTooltip>Copy Password</LmTooltip>
              </q-btn>
              <LmTooltip>{{ row.password }}</LmTooltip>
            </Grid>

            <span v-else>N/A</span>
          </GridTableCell>

          <GridTableCell class="grid items-center justify-center" no-tooltip>
            <Grid gaf="column" align-items="center">
              <DotsIcon :popup-items="getPopupItems(row)" @popup-item="onPopupItem($event.name, row)" />
              <Grid>
                <LmTooltip>
                  <Grid gap="var(--r-space-sm)">
                    <Grid gtc="auto 1fr" align-items="center" gap="var(--r-space-sm)">
                      <i class="fas fa-tag" />
                      <span class="text-no-wrap">{{ capitalizeSnakeCase(row.category.name) }}</span>
                    </Grid>

                    <Grid gtc="auto 1fr" align-items="center" gap="var(--r-space-sm)">
                      <i class="fas fa-user" />
                      <span class="text-no-wrap"> {{ row.created_by.name }} </span>
                    </Grid>

                    <Grid gtc="auto 1fr" align-items="center" gap="var(--r-space-sm)">
                      <i class="fas fa-clock" />
                      <span class="text-no-wrap"> {{ formatDateForCustomDisplay(row.updated_at, 'D MMM. Y') }} </span>
                    </Grid>
                  </Grid>
                </LmTooltip>
                <InfoIcon />
              </Grid>
            </Grid>
          </GridTableCell>
        </template>
      </template>
    </GridTable>
  </RCard>
</template>

<script>
import { DataService } from '@/api'
import { copyToClipboard } from '@/utils/general'
//Components
import FormCreateLink from '@/components/forms/FormCreateLink'
import PlusIcon from '@/ui/icons/PlusIcon'
import GridTable from '@/reusable/grid-table/GridTable'
import GridTableCell from '@/reusable/grid-table/GridTableCell'
import DotsIcon from '@/ui/icons/DotsIcon'
import InfoIcon from '@/ui/icons/InfoIcon'

export default {
  name: 'LmLinks',

  components: {
    PlusIcon,
    FormCreateLink,
    GridTable,
    GridTableCell,
    DotsIcon,
    InfoIcon
  },

  props: {
    entity: {
      type: Object,
      required: true
    },

    storeNamespace: {
      type: String,
      required: true
    },

    dropdownInitialValue: {
      type: Array,
      default: () => []
    }
  },

  provide: function() {
    return {
      service: this.getService()
    }
  },

  data: function() {
    return {
      showForm: false,
      editable: null,
      linksCollection: null,
      categoriesOptions: [],
      selectedCategories: this.dropdownInitialValue
    }
  },

  computed: {
    tableColumns() {
      return [
        { label: 'Name', width: '1fr' },
        { label: 'Link', width: '1fr' },
        { label: 'Username', width: '1fr' },
        { label: 'Password', width: '1fr' },
        { label: 'Actions', width: '55px' }
      ]
    },

    tableRows() {
      if (!this.linksCollection) return null

      return this.linksCollection.reduce((acc, curr) => {
        const links = curr.links.map(link => ({ ...link, entity: curr.entity }))

        if (curr.entity !== this.entity.name) {
          acc.push({ isGroup: true, data: curr })
        }
        acc.push(...links)

        return acc
      }, [])
    },

    serviceNamespace() {
      return `${this.storeNamespace}/${this.entity.id}/links`
    },

    selectedCategoryIds() {
      // get array of ids of the selected categories
      if (!this.categoriesOptions.length) return []
      return this.selectedCategories.map(selected => {
        return this.categoriesOptions.find(option => option.name === selected).value
      })
    },

    areAllOptionsSelected() {
      return this.selectedCategories.length === this.categoriesOptions.length
    }
  },

  watch: {
    selectedCategories: {
      handler: 'getLinks',
      immediate: true
    }
  },

  methods: {
    copyToClipboard,

    showLinkForm() {
      this.showForm = true
    },

    getService() {
      return new DataService(this.serviceNamespace)
    },

    getLinks() {
      const service = this.getService()
      service
        .get({
          context: {
            grouped: true,
            'filter[category]': this.selectedCategories.join()
          }
        })
        .then(response => (this.linksCollection = response.data.result))
    },

    editLink({ ...link }) {
      link.category_id = link.category.id
      this.$set(this, 'editable', link)
      this.showLinkForm()
    },

    deleteLink(link) {
      const service = this.getService()
      service.delete({ id: link.id }).then(() => {
        this.getLinks()
      })
    },

    closeForm() {
      this.showForm = false
      this.$set(this, 'editable', null)
    },

    setCategoriesOptions(categories) {
      this.categoriesOptions = categories
    },

    getPopupItems(row) {
      return [
        { name: 'edit', label: 'Edit' },
        { name: 'delete', label: 'Delete', textColorClass: 'text-danger-1', disable: row.entity !== this.entity.name }
      ]
    },

    onPopupItem(eventName, row) {
      if (eventName === 'delete') this.deleteLink(row)
      else if (eventName === 'edit') this.editLink(row)
    },

    handleAllOptionClick() {
      if (this.areAllOptionsSelected) this.selectedCategories = []
      else this.selectedCategories = this.categoriesOptions.map(o => o.name)
    }
  }
}
</script>

<style lang="scss" scoped>
.links {
  .link {
    text-decoration: none;
  }
}
</style>
